import { trpc } from '@tencent/tpro_rmonitor_web_server_bitmap';
import {
  Card,
  Divider,
} from 'antd';
import { get, isArray, unset, trimStart, trimEnd, isEmpty, isFinite } from 'lodash-es';
import moment from 'moment';
import React, {
  FC,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getParamFromUrl, replaceHistory, toSearchObj } from '@/infrastructure/history';
import { isElectron } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import {
  DATE_TIME_FORMAT,
  formatExactTimeRange,
  formatTimeRange,
  getAndFormatDateRangeByShortCut,
  getDateRangeByShortCut,
  transform2TimeRange,
} from '@/infrastructure/utils/timestamp';
import { toArray } from '@/infrastructure/utils/to-array';
import { ExceptionForm, getUrlFilterParams } from '@/presentation/components/exception-form';
import {
  detailLocalStorage, detailFilterRule,
  reportEditSearchCriteria, SelectNumberFilter, getExceptionTypeIds,
} from '@/presentation/components/exception-form/utils';
import { NumberType } from '@/presentation/components/form/components/select-number';
import { DEFAULT_TIME_RANGE_ISSUES } from '@/presentation/components/form/components/time-range';
import {
  BUGLY_DATE_RANGE_BY_SHORT_CUT, shortRanges,
} from '@/presentation/components/form/components/time-range-cascade';
import {
  ClusterStackType, FieldsOperator,
} from '@/presentation/components/form/proto';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import { getPgIdByIssueType } from '@/presentation/components/form/utils';
import { TimeRangeType } from '@/presentation/components/issue';
import { AreaNameEnum } from '@/presentation/components/tds-form/tds-area';
import { ProblemForm as AnrIOSProblemForm } from '@/presentation/pages/anr/issues-ios/list/form';
import { PowerDetailProblemForm } from '@/presentation/pages/battery/issues/detail/form';
import { CpuListForm } from '@/presentation/pages/cpu/list/form';
import { ProblemForm as DropFormProblemForm } from '@/presentation/pages/drop-frame/issues/list/form';
import { ProblemForm as FoomIOSProblemForm } from '@/presentation/pages/foom/case/list/form';
import { ProblemForm as FoomMallocProblemForm } from '@/presentation/pages/foom/malloc/list/form';
import { KuiklyErrorForm } from '@/presentation/pages/kuikly/error/form';
import { ProblemForm as BadMallocProblemForm } from '@/presentation/pages/memory/bad-malloc/list/form';
import { ProblemForm as BigPcProblemForm } from '@/presentation/pages/memory/big-pic/list/form';
import { ProblemForm as FDProblemForm } from '@/presentation/pages/memory/fd/list/form';
import { ProblemForm as MemoryHighProblemForm } from '@/presentation/pages/memory/high/list/form';
import { JavaLeakProblemForm } from '@/presentation/pages/memory/java-leak/list/pro-form';
import { MemoryDetailProblemForm } from '@/presentation/pages/memory/memory-detail/list/form';
import { ProblemForm as NativeProblemForm } from '@/presentation/pages/memory/native/list/form';
import { ProblemForm as VcLeakProblemForm } from '@/presentation/pages/memory/vc-leak/list/form';
import { NetHttpProblemForm } from '@/presentation/pages/net/http/list/form';
import { NetProblemForm } from '@/presentation/pages/traffic/issues/list/form';
import { FLOW_OPTIONS } from '@/presentation/pages/traffic/issues/list/table/flow';
import { PropsWithStyles } from '@/proto/common';
import { CommonFieldKey, FIELD_ALL, FieldKey } from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { MetricType } from '@/proto/metric';
import {
  appVersionField2Filter,
  filterTransformMap,
  getBuglyToken, getFilterKeyForCustomTag, getIssueListToken,
  getToken, isValidFilter, KeyDataType,
  parseAnaToken,
  parseBuglyToken,
  parseToken,
} from '@/proto/token';
import { WithTypeCast } from '@raft/utils';
import { toHumpString } from '@raft/utils/src/case/camel-case';
import {
  toLineString,
  toSnakeCase,
} from '@raft/utils/src/case/snake-case';
import { ITdsFormRef } from '../tds-form';
type IBizExtendInfo = WithTypeCast<trpc.rmonitor.web_server.IBizExtendInfo>;


interface IProps {
  issueType: IssueType;
  metricType?: MetricType;
  entry: FilterEntry;
  onTokenChange?: (token: string) => void;
  onChangeParams?: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  formInitialValue?: Record<string, any>;
  onInitFields?: (fields: Record<string, any>) => void;
  defaultParams?: Record<string, any>;
  formProps?: Record<string, any>;
}

const entryFormMap = {
  [FilterEntry.PowerExceptionTypeDetail]: PowerDetailProblemForm,
  [FilterEntry.PowerExceptionTypeAnalysis]: PowerDetailProblemForm,
  [FilterEntry.DropFrameDetail]: DropFormProblemForm,
  [FilterEntry.DropFrameAnalysis]: DropFormProblemForm,
  [FilterEntry.MemoryJavaLeakDetail]: JavaLeakProblemForm,
  [FilterEntry.MemoryJavaLeakAnalysis]: JavaLeakProblemForm,
  [FilterEntry.MemoryFdDetail]: FDProblemForm,
  [FilterEntry.MemoryFdAnalysis]: FDProblemForm,
  [FilterEntry.MemoryNativeDetail]: NativeProblemForm,
  [FilterEntry.MemoryNativeAnalysis]: NativeProblemForm,
  [FilterEntry.MemoryBigPcDetail]: BigPcProblemForm,
  [FilterEntry.MemoryBigPcAnalysis]: BigPcProblemForm,
  [FilterEntry.MemoryVcLeakDetail]: VcLeakProblemForm,
  [FilterEntry.MemoryVcLeakAnalysis]: VcLeakProblemForm,
  [FilterEntry.MemoryBadMallocDetail]: BadMallocProblemForm,
  [FilterEntry.MemoryBadMallocAnalysis]: BadMallocProblemForm,
  [FilterEntry.FoomIOSDetail]: FoomIOSProblemForm,
  [FilterEntry.FoomIOSAnalysis]: FoomIOSProblemForm,
  [FilterEntry.MemoryHighDetail]: MemoryHighProblemForm,
  [FilterEntry.MemoryHighAnalysis]: MemoryHighProblemForm,
  [FilterEntry.FoomMallocDetail]: FoomMallocProblemForm,
  [FilterEntry.FoomMallocAnalysis]: FoomMallocProblemForm,
  [FilterEntry.ANRIOSDetail]: AnrIOSProblemForm,
  [FilterEntry.ANRIOSAnalysis]: AnrIOSProblemForm,
  [FilterEntry.NetDetail]: NetProblemForm,
  [FilterEntry.NetAnalysis]: NetProblemForm,
  [FilterEntry.NetHttpDetail]: NetHttpProblemForm,
  [FilterEntry.NetHttpAnalysis]: NetHttpProblemForm,
  [FilterEntry.NetHttpDomainDetail]: NetHttpProblemForm,

  [FilterEntry.MemoryDetailDetail]: MemoryDetailProblemForm,
  [FilterEntry.MemoryDetailAnalysis]: MemoryDetailProblemForm,
  [FilterEntry.CpuDetails]: CpuListForm,
  [FilterEntry.CpuAnalysis]: CpuListForm,
  [FilterEntry.KuiklyKotlinDetail]: KuiklyErrorForm,
  [FilterEntry.KuiklyKotlinAnalysis]: KuiklyErrorForm,
};

// 根据字符匹配决定要用什么规则
const getMatchRule = (text: string) => {
  const matchRule = {
    value: text,
    rule: '',
  };
  const matchSymbol = '*';
  const isPrefixMatch = text.endsWith(matchSymbol);
  const isSuffixMatch = text.startsWith(matchSymbol);
  if (isPrefixMatch && isSuffixMatch) {
    matchRule.rule = 'MATCH';
    matchRule.value = trimStart(trimEnd(text, matchSymbol), matchSymbol);
  } else if (isPrefixMatch) {
    matchRule.rule = 'PREFIX';
    matchRule.value = trimEnd(text, matchSymbol);
  } else if (isSuffixMatch) {
    matchRule.rule = 'SUFFIX';
    matchRule.value = trimStart(text, matchSymbol);
  }

  return matchRule;
};

// key-value 转 Filter
export const generateFormList = (params: Record<string, any>) => {
  const timeCostFieldValue = params?.standardLaunchDuration?.value ?? '';
  const timeCostValue = (!isEmpty(timeCostFieldValue) && timeCostFieldValue?.[0] && timeCostFieldValue?.[1])
    ? `${timeCostFieldValue?.[0]},${timeCostFieldValue?.[1]}` : `${timeCostFieldValue}`;
  const freeMemFieldValue = params?.standardFreeMem?.value ?? '';

  const singleFreeMemValue = isFinite(freeMemFieldValue?.[0]) && freeMemFieldValue !== '' ? freeMemFieldValue?.[0] * 1024 * 1024 * 1024 : freeMemFieldValue;
  const freeMemValue = (!isEmpty(freeMemFieldValue) && freeMemFieldValue?.[0] && freeMemFieldValue?.[1])
    ? `${freeMemFieldValue?.[0] * 1024 * 1024 * 1024},${freeMemFieldValue?.[1] * 1024 * 1024 * 1024}` : `${singleFreeMemValue}`;

  const startTime = moment(params.startDateStr).unix();
  const endTime = moment(params.endDateStr).unix();

  const exceptionTypeList = params.exceptionTypeList ? getExceptionTypeIds(params.exceptionTypeList) : '';

  const clientCountry = isElectron() ? AreaNameEnum.ElectronClientCountry : AreaNameEnum.ClientCountry;
  const clientProvince = isElectron() ? AreaNameEnum.ElectronClientProvince : AreaNameEnum.ClientProvince;
  const clientCity = isElectron() ? AreaNameEnum.ElectronClientCity : AreaNameEnum.ClientCity;

  const filters: {key?: string, name: string; values: string[]; operator: string;type?: number }[] = [
    appVersionField2Filter(params),
    {
      name: params.timeType === 'occur' ? 'report_time' : 'event_time',
      values: [`${params.startTime || params.start_time || startTime}`],
      operator: 'GE',
    },
    {
      name: params.timeType === 'occur' ? 'report_time' : 'event_time',
      values: [`${params.endTime || params.end_time || endTime}`],
      operator: 'LE',
    },
    {
      name: FieldKey.AccountId,
      values: toArray((params?.[FieldKey.StandardAccountId]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardAccountId]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Arguments,
      values: toArray((params?.[FieldKey.StandardArguments]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardArguments]?.mode || MatchMode.OR,
    },
    {
      name: 'device_id',
      values: toArray((params?.[FieldKey.StandardQimei]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardQimei]?.mode || MatchMode.OR,
    },
    {
      name: 'qimei',
      values: toArray((params?.standardDeviceId?.values ?? '') as string),
      operator: params?.standardDeviceId?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Stack,
      values: toArray((params?.standardStack?.values ?? '') as string),
      operator: params?.standardStack?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.AppInBack,
      values: toArray(params?.standardAppInBack),
      operator: MatchMode.OR,
    },
    {
      name: 'launch_duration',
      values: toArray(timeCostValue),
      operator: params?.standardLaunchDuration?.operator,
    },
    {
      name: 'free_mem',
      values: toArray(freeMemValue),
      operator: params?.standardFreeMem?.operator,
    },
    {
      name: 'oom_type',
      values: toArray(params?.oomType),
      operator: MatchMode.OR,
    },
    {
      name: 'type',
      values: toArray(exceptionTypeList),
      operator: '',
    },
    {
      name: 'device_type',
      values: toArray(params?.deviceType),
      operator: MatchMode.OR,
    },
    {
      name: FieldKey.BundleId,
      values: toArray((params?.[FieldKey.StandardBundleId]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardBundleId]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ChannelId,
      values: toArray((params?.standardChannelId?.values ?? '') as string),
      operator: params?.standardChannelId?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ProcessType,
      values: toArray((params?.standardProcessType?.values ?? '') as string),
      operator: params?.standardProcessType?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Stage,
      values: toArray((params?.standardStage?.values ?? '') as string),
      operator: params?.standardStage?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Brand,
      values: toArray((params?.[FieldKey.StandardBrand]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardBrand]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.ShiplyTag),
      values: toArray((params?.[FieldKey.StandardShiplyTag]?.values as string[]) ?? ''),
      operator: params?.[FieldKey.StandardShiplyTag]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
      values: toArray((params?.[FieldKey.StandardAbTestTag]?.values as string[]) ?? ''),
      operator: params?.[FieldKey.StandardAbTestTag]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.TagsInFilterUnderline,
      values: toArray((params?.[FieldKey.StandardTagsInFilter]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardTagsInFilter]?.mode || MatchMode.OR,
      key: 'a13',
      type: KeyDataType.ARRAY,
    },
    {
      name: 'cpu_arc',
      values: toArray((params?.standardCpuArch?.values ?? '') as string),
      operator: params?.standardCpuArch?.mode || MatchMode.OR,
    },
    {
      name: 'cpu_architecture',
      values: toArray((params?.standardCpuArchitecture?.values ?? '') as string),
      operator: params?.standardCpuArchitecture?.mode || MatchMode.OR,
    },
    {
      name: 'arch',
      values: toArray((params?.standardElectronCpuArch?.values ?? '') as string),
      operator: params?.standardElectronCpuArch?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.SdkVersion,
      values: toArray(params?.[FieldKey.StandardSdkVersion]?.values as string[]),
      operator: params?.[FieldKey.StandardSdkVersion]?.mode || MatchMode.OR,
    },
    {
      name: 'issue_uuid',
      values: toArray((params?.standardIssueId?.values ?? '') as string),
      operator: params?.standardIssueId?.mode || MatchMode.OR,
    },
    {
      name: 'crash_id',
      values: toArray((params?.standardCrashId?.values ?? '') as string),
      operator: params?.standardCrashId?.mode || MatchMode.OR,
    },
    {
      name: 'client_identify',
      values: toArray((params?.standardClientIdentify?.values ?? '') as string),
      operator: params?.standardClientIdentify?.mode || MatchMode.OR,
    },
    {
      name: 'exception_type',
      values: toArray((params?.standardErrorType?.values ?? '') as string),
      operator: params?.standardErrorType?.mode || MatchMode.OR,
    },
    {
      name: 'model',
      values: toArray((params?.standardHardware?.values ?? '') as string),
      operator: params?.standardHardware?.mode || MatchMode.OR,
    },
    {
      name: 'os',
      values: toArray((params?.[FieldKey.StandardOsVersion]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardOsVersion]?.mode || MatchMode.OR,
    },
    {
      name: 'process_name',
      values: toArray((params?.standardProcessName?.values ?? '') as string),
      operator: params?.standardProcessName?.mode || MatchMode.OR,
    },
    {
      name: 'pid',
      values: toArray((params?.standardProcessId?.values ?? '') as string),
      operator: params?.standardProcessId?.mode || MatchMode.MATCH,
    },
    {
      name: 'thread_name',
      values: toArray((params?.standardThreadName?.values ?? '') as string),
      operator: params?.standardThreadName?.mode || MatchMode.OR,
    },
    {
      name: 'rom_name',
      values: toArray((params?.standardRom?.values ?? '') as string),
      operator: params?.standardRom?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ContinuousEvent,
      values: toArray((params?.standardContinuousEvent ?? '')),
      operator: MatchMode.OR,
    },
    {
      name: 'linkages',
      values: toArray((params?.standardLinkages?.values ?? '') as string),
      operator: params?.standardRom?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'scene_labels',
      values: toArray((params?.[FieldKey.StandardSceneDataLabels]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardSceneDataLabels]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'files',
      values: toArray((params?.[FieldKey.StandardAttachFileNames]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardAttachFileNames]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'main_stack',
      values: toArray((params?.standardMainStack?.values ?? '') as string),
      operator: params?.standardMainStack?.mode || MatchMode.OR,
    },
    {
      name: 'build_number',
      values: toArray((params?.standardBuildNumber?.values ?? '') as string),
      operator: params?.standardBuildNumber?.mode || MatchMode.OR,
    },
    {
      name: 'asan',
      values: toArray((params?.[FieldKey.StandardAsanType] ?? '') as string),
      operator: params?.[FieldKey.StandardAsanType]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Linkages,
      values: toArray(params?.[FieldKey.Linkages]),
      operator: FieldsOperator.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'inferred_labels',
      values: toArray((params?.[FieldKey.StandardInferredLabels]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardInferredLabels]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: clientCountry,
      values: toArray(params?.[FieldKey.StandardArea]
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === clientCountry)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: clientProvince,
      values: toArray(params?.[FieldKey.StandardArea]
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === clientProvince)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: clientCity,
      values: toArray(params?.[FieldKey.StandardArea]
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === clientCity)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: 'model_name',
      values: toArray((params?.[FieldKey.StandardLlmModel]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardLlmModel]?.mode || MatchMode.OR,
    },
    {
      name: CommonFieldKey.NetworkType,
      values: toArray((params?.[FieldKey.StandardNetworkType]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardNetworkType]?.mode || MatchMode.OR,
    },
    {
      name: 'span_name',
      values: toArray((params?.[FieldKey.StandardSpanName]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardSpanName]?.mode || MatchMode.OR,
    },
    {
      name: 'model',
      values: toArray((params?.[FieldKey.StandardModel]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardModel]?.mode || MatchMode.OR,
    },
  ];

  // 处理自定义字段filters
  const bizExtendInfo = params.bizExtendInfo?.map((bizExtend: IBizExtendInfo) => ({
    ...bizExtend,
    name: 'user_classify_data',
    operator: bizExtend.operator,
  })) ?? [];

  // 处理自定义维度filters
  const userCustom = params.userCustom?.map((userCustom: IBizExtendInfo) => ({
    ...userCustom,
    name: 'user_custom',
    operator: userCustom.operator,
  })) ?? [];

  const tapdStatusArray = Array.isArray(params.tapdStatus) ? params.tapdStatus : [params.tapdStatus];
  const formList: Record<string, any> = {
    start_time: startTime,
    end_time: endTime,
    detail_mode: params.detailMode,
    is_participle: params.isParticiple,
    detail: params.detail,
    parse_field: {
      time_type: params.timeType,
      time_quick: params.timeQuick,
      app_version_operator: params.appVersionOperator,
      min_app_version: params.minAppVersion,
      max_app_version: params.maxAppVersion,
      wildcard_app_version: params.wildcardAppVersion,
    },
    tapd_status: params.tapdStatus ? tapdStatusArray : [''],
  };

  if (params.stage) {
    formList.scene = params.stage;
  }

  Object.keys(params).forEach((paramKey) => {
    const transformKey = filterTransformMap[paramKey];
    if (transformKey) {
      const value: string | string[] | NumberType = params[paramKey];
      if (transformKey === filterTransformMap.productVersion) {
        // 改版后的组件这里要重新处理下，判断是不是空值，有可能是[]或者['']或者''
        const formatValues = isArray(value) ? value.join('') : value;
        if (formatValues) {
          filters.push({
            name: toLineString(transformKey),
            values: value as string[],
            operator: '',
          });
        }
      } else if (transformKey === filterTransformMap.stack) {
        detailFilterRule(params, transformKey, filters);
      } else if (transformKey === filterTransformMap.appInBacks && value) {
        if (value === '0') {
          return;
        }
        const appInBack = ['', '0', 'true', 'false'];
        filters.push({
          name: toLineString(transformKey),
          values: [appInBack[Number(value)]],
          operator: '',
        });
      } else if ((transformKey === filterTransformMap.launchDuration || transformKey === filterTransformMap.freeMem) && !Array.isArray(value) && typeof(value) === 'object' && value?.value?.length) {
        filters.push(...SelectNumberFilter(value, transformKey, true));
      } else if (typeof value === 'string' && value !== FIELD_ALL) {
        const matchRule = getMatchRule(value);
        filters.push({
          name: toLineString(transformKey),
          values: [matchRule.value],
          operator: matchRule.rule,
        });
      }
    }
  });

  const newFilters = filters.concat(bizExtendInfo).concat(userCustom)
    .filter(item => isValidFilter(item));
  return toSnakeCase({
    ...formList,
    filters: newFilters,
  });
};

const dateFormat = 'YYYY-MM-DD HH:mm:ss';

export const ProblemDetailFilter: FC<PropsWithStyles<IProps>> = (props) => {
  const {
    issueType,
    metricType,
    entry,
    onTokenChange,
    onChangeParams,
    formInitialValue = {},
    className,
    defaultParams } = props;
  const [initFields, setInitFields] = useState<any>(formInitialValue || null);
  const [tokenParams, setTokenParams] = useState<any>(null);
  const history = useHistory();
  const { pathname } = useLocation();
  const clusterStackType = getParamFromUrl('clusterStackType') === '1' && issueType === IssueType.ISSUE_TYPE_ANR_BUGLY ? ClusterStackType.ANR_TRACE_MAIN_STACK : undefined;
  const isBuglyDetail = [
    FilterEntry.ANRDetail,
    FilterEntry.CrashDetail,
    FilterEntry.ErrorDetail,
    FilterEntry.FoomDetail,
    FilterEntry.CrashCaller,
    FilterEntry.AndroidANRCaller,
    FilterEntry.KuiklyKotlinDetail,
    FilterEntry.ErrorCaller
  ].includes(entry);
  const isBuglyAnalysis = [
    FilterEntry.ANRAnalysis,
    FilterEntry.CrashAnalysis,
    FilterEntry.ErrorAnalysis,
    FilterEntry.FoomAnalysis,
    FilterEntry.KuiklyKotlinAnalysis,
  ].includes(entry);

  const isMonitorDetail = [
    FilterEntry.DropFrameDetail,
    FilterEntry.MemoryJavaLeakDetail,
    FilterEntry.MemoryFdDetail,
    FilterEntry.MemoryNativeDetail,
    FilterEntry.MemoryBigPcDetail,
    FilterEntry.MemoryVcLeakDetail,
    FilterEntry.MemoryBadMallocDetail,
    FilterEntry.FoomIOSDetail,
    FilterEntry.MemoryHighDetail,
    FilterEntry.FoomMallocDetail,
    FilterEntry.ANRIOSDetail,
    FilterEntry.NetDetail,
    FilterEntry.PowerExceptionTypeDetail,
    FilterEntry.MemoryDetailDetail,
  ].includes(entry);

  // 新的表单，可以跳过这里的初始化逻辑
  const newFormList = [
    FilterEntry.FoomIOSDetail,
    FilterEntry.FoomIOSAnalysis,
    FilterEntry.MemoryHighDetail,
    FilterEntry.MemoryHighAnalysis,
  ];

  const tdsFormRef = useRef<ITdsFormRef>(null);


  const initParseBuglyToken = async (token: string) => {
    const fields = await parseBuglyToken(token);
    const defaultStartDateStr = moment().subtract(6, 'days')
      .startOf('date')
      .format(dateFormat);
    const defaultEndDateStr = moment().endOf('date')
      .format(dateFormat);
    const { startDateStr = defaultStartDateStr, endDateStr = defaultEndDateStr, ...resetFields } = fields;
    const isDateRangeByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(fields?.timeQuick ?? '');
    const dateRange = getDateRangeByShortCut(fields?.timeQuick ?? '', shortRanges);
    // 判断是否是否为当天，否则显示自定义
    const isToday = isDateRangeByShortCut && moment(endDateStr, DATE_TIME_FORMAT).unix() === dateRange?.[1]?.unix?.();

    const timeRange = isDateRangeByShortCut && isToday ? [
      moment(dateRange[0]).format(dateFormat),
      moment(dateRange[1]).format(dateFormat),
    ] : [startDateStr, endDateStr];

    if (!isToday && isDateRangeByShortCut) {
      resetFields.timeQuick = 'custom';
    }

    const params = {
      timeRange,
      ...resetFields,
      ...formInitialValue,
    };
    setInitFields(params);
    setTokenParams(params);
    return params;
  };

  const initParseToken = async (token: string, transformBuglyFields?: boolean) => {
    const tokenService = transformBuglyFields ? parseAnaToken : parseToken;
    const fields = await tokenService(token);
    const { startTime, endTime, ...resetFields } = fields;
    const isDateRangeByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(fields?.timeQuick ?? '');
    const dateRange = getDateRangeByShortCut(fields?.timeQuick, shortRanges);
    // 判断是否超过当天，否则显示自定义

    const isToday = Number(endTime) === dateRange?.[1]?.unix?.();
    const timeRange = isDateRangeByShortCut && isToday
      ? transform2TimeRange(
        moment(dateRange[0]).unix()
          .toString(),
        moment(dateRange[1]).unix()
          .toString(),
      ) : transform2TimeRange(startTime, endTime);
    if (!isToday && isDateRangeByShortCut) {
      resetFields.timeQuick = 'custom';
    }

    const params = {
      ...timeRange,
      ...resetFields,
      ...formInitialValue,
    };

    setInitFields(params);
    setTokenParams(params);
    return params;
  };

  const onDeletedKeys = async (keys: string[]) => {
    const tempParams = { ...tokenParams };
    unset(tempParams, keys);
    setTokenParams(tempParams);
    const tokenService = getTokenService();
    const token = await tokenService(tempParams);
    updateUrlToken(token);
  };

  useEffect(() => {
    const load = async () => {
      const token = getParamFromUrl('token');
      const urlQuery = getUrlFilterParams();
      if (!token) {
        let params = {
          timeRange: DEFAULT_TIME_RANGE_ISSUES,
        };

        if (isBuglyDetail) {
          params = { ...params, ...urlQuery };
        }
        const initFields = {
          ...params,
          ...formInitialValue,
          timeQuick: isBuglyAnalysis ? 'custom' : formInitialValue?.timeQuick ?? 'custom',
        };
        setInitFields(initFields);
        props.onInitFields?.(initFields);
        await onFormChanged(initFields);
      } else if (isBuglyAnalysis) {
        const params = await initParseBuglyToken(token);
        props.onInitFields?.(params);
        await onFormChanged(params);
      } else if (isBuglyDetail) {
        const params = await initParseBuglyToken(token);

        props.onInitFields?.(params);
      } else {
        const params = await initParseToken(token);
        props.onInitFields?.(params);
      }
    };
    // 新表单可以跳过这里的初始化
    if (newFormList.includes(entry)) {
      return;
    }
    load();
  }, []);

  const updateUrlToken = (token: string) => {
    const searchObj = {
      ...toSearchObj(window.location.search),
      token,
    };
    replaceHistory(history, pathname, searchObj);
  };

  const getTokenService = () => ((isBuglyDetail || isBuglyAnalysis) ? getBuglyToken : getToken);

  const onFormChanged = async (values: Record<string, any>) => {
    let token;
    const { timeRange, customDate, bizExtendInfo, ...fields } = values;
    const isDateRangeByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(fields?.timeQuick ?? '');
    const dateParams: Record<string, string> = {
      date: 'custom',
    };
    const tokenService = getTokenService();
    onChangeParams?.(values);

    if (customDate) {
      const dateRange = getDateRangeByShortCut(customDate);
      dateParams.startDateStr = dateRange[0].format(dateFormat);
      dateParams.endDateStr = dateRange[1].format(dateFormat);
    } else if (isDateRangeByShortCut) {
      const dateRange = getDateRangeByShortCut(fields?.timeQuick, shortRanges);
      dateParams.startDateStr = dateRange[0]?.format(dateFormat);
      dateParams.endDateStr = dateRange[1]?.format(dateFormat);
    } else {
      dateParams.startDateStr = (isMonitorDetail || isBuglyDetail)
        ? moment(timeRange[0]).format(dateFormat)
        : moment(timeRange[0]).startOf('date')
          .format(dateFormat);
      dateParams.endDateStr = (isMonitorDetail || isBuglyDetail)
        ? moment(timeRange[1]).format(dateFormat)
        : moment(timeRange[1]).endOf('date')
          .format(dateFormat);
    }

    if (isBuglyDetail) {
      const params: Record<string, any> = {
        bizExtendInfo,
        ...dateParams,
        ...fields,
        clusterStackType,
        ...defaultParams,
      };
      setTokenParams(params);
      token = await getIssueListToken(params);
      if (isDateRangeByShortCut) {
        setInitFields({
          ...params,
          timeRange: [
            moment(params?.startDateStr).format(dateFormat),
            moment(params?.endDateStr).format(dateFormat),
          ],
        });
      }
    } else if (isBuglyAnalysis) {
      const ranges = customDate ? getAndFormatDateRangeByShortCut(customDate) : formatTimeRange(timeRange as string[]);
      const formatValues = {
        ...fields,
        ...ranges,
      };

      setTokenParams({ ...formatValues, clusterStackType, bizExtendInfo });
      token = await getIssueListToken({ ...formatValues, clusterStackType, bizExtendInfo });
    } else {
      const { timeRange, ...fields } = values;
      let ranges: TimeRangeType = isMonitorDetail
        ? formatExactTimeRange(timeRange as string[])
        : formatTimeRange(timeRange as string[]);
      if (isDateRangeByShortCut) {
        const dateRange = getDateRangeByShortCut(fields?.timeQuick, shortRanges);
        ranges = getAndFormatDateRangeByShortCut(fields?.timeQuick, shortRanges);
        if (customDate) {
          ranges = getAndFormatDateRangeByShortCut(customDate);
        }
        setInitFields({
          ...ranges,
          ...fields,
          timeRange: [
            moment(dateRange[0]).format(dateFormat),
            moment(dateRange[1]).format(dateFormat),
          ],
          isValueInit: false,
        });
      }

      const params: Record<string, any> = {
        ...ranges,
        ...fields,
        clusterStackType,
      };

      // 兼容 异常分析=》流量归因 的域名、URL、网络库的跳转详情筛选
      const trafficName = getParamFromUrl('trafficName');
      const trafficValue = getParamFromUrl('trafficValue');
      if (trafficName && trafficValue && FLOW_OPTIONS.some(item => item.value === trafficName)) {
        params[toHumpString(trafficName)] = trafficValue;
      }

      setTokenParams(params);
      token = await tokenService(params);
    }
    if (Array.isArray(values?.detail) && values?.detail?.length) {
      const paramsDetail = values.detail;
      detailLocalStorage(paramsDetail);
    }
    updateUrlToken(token);
    onTokenChange?.(token);
    const pgId = getPgIdByIssueType(issueType, entry);
    reportEditSearchCriteria(values, pgId);
    reportClick(pgId, CUSTOM_ACTIONS.commonDoSearch, {
      from: pgId,
    });
  };
  const renderFormComponent = () => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const CustomFromComponent: any = get(entryFormMap, entry);
    if (!CustomFromComponent) {
      return <ExceptionForm
        onFinish={onFormChanged}
        issueType={issueType}
        metricType={metricType!}
        entry={entry}
        initialValue={initFields}
        onDeletedKeys={onDeletedKeys}
        defaultValues={initFields}
      />;
    }

    return <CustomFromComponent
      onFinish={onFormChanged}
      entry={entry}
      name={entry}
      initialValue={initFields}
      onValueInited={onFormChanged}
      onTokenChange={onTokenChange}
      // 放在上面，保证可以被外面自定义的覆盖掉
      tdsFormRef={tdsFormRef}
      tokenInitValues={initFields}
      {...props.formProps}
    />;
  };

  return <>
    <Card bordered={false} className={className}>
      {renderFormComponent()}
    </Card>
    <Divider style={{ margin: 0 }} />
  </>;
};
